import { useCallback, useRef, useState } from "react";
import { graphql, useMutation } from "react-relay";

import type { useMarkConversationReadMutationMarkConversationReadMutation } from "./__generated__/useMarkConversationReadMutationMarkConversationReadMutation.graphql";

const markConversationReadMutation = graphql`
  mutation useMarkConversationReadMutationMarkConversationReadMutation(
    $input: MarkConversationReadInput!
  ) {
    markConversationRead(input: $input) {
      success
      conversation {
        id
        hasUnreadResponse
      }
    }
  }
`;

const DEBOUNCE_MS = 2000;

export const useMarkConversationReadMutation = () => {
  const [commit] = useMutation<useMarkConversationReadMutationMarkConversationReadMutation>(
    markConversationReadMutation
  );
  const recentlyFiredRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [markedReadIds, setMarkedReadIds] = useState<Set<string>>(new Set());

  const markAsRead = useCallback(
    (conversationId: string, onCompleted?: () => void) => {
      // Skip mutation if we already fired for this conversation recently
      if (recentlyFiredRef.current.has(conversationId)) {
        return;
      }

      setMarkedReadIds((prev) => {
        if (prev.has(conversationId)) return prev;
        const next = new Set(prev);
        next.add(conversationId);
        return next;
      });

      commit({
        variables: { input: { conversationId } },
        optimisticResponse: {
          markConversationRead: {
            __typename: "MarkConversationReadPayload",
            success: true,
            conversation: {
              __typename: "GraphQLLlmConversation",
              id: conversationId,
              hasUnreadResponse: false,
            },
          },
        },
        onCompleted: () => {
          onCompleted?.();
        },
        onError: () => {
          setMarkedReadIds((prev) => {
            const next = new Set(prev);
            next.delete(conversationId);
            return next;
          });
        },
      });

      const timer = setTimeout(() => {
        recentlyFiredRef.current.delete(conversationId);
      }, DEBOUNCE_MS);
      recentlyFiredRef.current.set(conversationId, timer);
    },
    [commit]
  );

  return { markAsRead, markedReadIds };
};
